/**
 * ACC hub → project → folder → file browse. State lives in accBrowseStore
 * so standalone and Forma surfaces stay in sync.
 */
import { useCallback, useEffect } from "preact/hooks";
import {
  isPdfExtension,
  isRevitExtension,
  isSubmissionFileExtension,
} from "../lib/submissionFileTypes";
import type {
  AccFolderEntry,
  AccHub,
  AccProject,
} from "../services/dataManagement";
import { DataManagementApiError } from "../services/dataManagement";
import { pickDefaultAccHub } from "../services/dataManagement/defaultAccHub";
import { dmRegionFromHub } from "../services/dataManagement/region";
import {
  type AccFileRow,
  type FolderCrumb,
  useAccBrowseStore,
} from "../stores/accBrowseStore";
import { useDocumentViewerStore } from "../stores/documentViewerStore";
import { useDataManagementService } from "./useDataManagementService";

function syncSelection(
  hub: AccHub,
  project: AccProject,
  crumbs: FolderCrumb[],
  fileRows: AccFileRow[],
  selectedItemId: string | null
): void {
  const { setSelection } = useAccBrowseStore.getState();
  if (!selectedItemId) {
    setSelection(null);
    return;
  }
  const file = fileRows.find(r => r.itemId === selectedItemId);
  if (!file) {
    setSelection(null);
    return;
  }
  setSelection({
    hubId: hub.id,
    hubName: hub.name,
    hubRegion: dmRegionFromHub(hub.region),
    projectId: project.id,
    projectName: project.name,
    itemId: file.itemId,
    fileName: file.fileName,
    folderPath: crumbs.map(c => c.name).join(" / "),
  });
}

export function useAccBrowse() {
  const dm = useDataManagementService();
  const hubs = useAccBrowseStore(s => s.hubs);
  const projects = useAccBrowseStore(s => s.projects);
  const hub = useAccBrowseStore(s => s.hub);
  const project = useAccBrowseStore(s => s.project);
  const projectDetails = useAccBrowseStore(s => s.projectDetails);
  const crumbs = useAccBrowseStore(s => s.crumbs);
  const sidebarFolders = useAccBrowseStore(s => s.sidebarFolders);
  const fileRows = useAccBrowseStore(s => s.fileRows);
  const activeFolderId = useAccBrowseStore(s => s.activeFolderId);
  const selectedItemId = useAccBrowseStore(s => s.selectedItemId);
  const selection = useAccBrowseStore(s => s.selection);
  const loading = useAccBrowseStore(s => s.loading);
  const loadingKind = useAccBrowseStore(s => s.loadingKind);
  const error = useAccBrowseStore(s => s.error);
  const initialized = useAccBrowseStore(s => s.initialized);

  const loadProjectDetails = useCallback(
    async (h: AccHub, p: AccProject) => {
      const { setProjectDetails } = useAccBrowseStore.getState();
      try {
        const details = await dm.getProject(h.id, p.id, h.region);
        setProjectDetails(details);
      } catch {
        setProjectDetails({ id: p.id, hubId: h.id, name: p.name });
      }
    },
    [dm]
  );

  const loadFolderContents = useCallback(
    async (
      h: AccHub,
      p: AccProject,
      folder: AccFolderEntry | FolderCrumb,
      parentCrumbs: FolderCrumb[]
    ) => {
      const st = useAccBrowseStore.getState();
      st.setHub(h);
      st.setProject(p);
      st.setActiveFolderId(folder.id);
      const nextCrumbs = [
        ...parentCrumbs,
        { id: folder.id, name: folder.name },
      ];
      st.setCrumbs(nextCrumbs);
      st.setLoadingKind("files");
      st.setError(null);
      try {
        const contents = await dm.getFolderContents(p.id, folder.id, h.region);
        const folders = contents.filter(e => e.objectType === "folders");
        const files: AccFileRow[] = contents
          .filter(e => e.objectType === "items")
          .filter(e => isSubmissionFileExtension(e.extension))
          .map(e => ({
            itemId: e.id,
            fileName: e.name,
            extension: e.extension ?? "",
          }));
        st.setSidebarFolders(folders);
        st.setFileRows(files);
        const selected = useAccBrowseStore.getState().selectedItemId;
        if (selected && !files.some(f => f.itemId === selected)) {
          st.setSelectedItemId(null);
          st.setSelection(null);
        } else {
          syncSelection(h, p, nextCrumbs, files, selected);
        }
      } catch (e) {
        st.setError(readError(e));
      } finally {
        st.setLoadingKind(null);
      }
    },
    [dm]
  );

  const openProject = useCallback(
    async (h: AccHub, p: AccProject) => {
      const st = useAccBrowseStore.getState();
      st.setHub(h);
      st.setProject(p);
      st.setCrumbs([]);
      st.setFileRows([]);
      st.setActiveFolderId(null);
      st.setSelectedItemId(null);
      st.setSelection(null);
      st.setLoadingKind("files");
      st.setError(null);
      void loadProjectDetails(h, p);
      try {
        const top = await dm.getProjectTopFolders(h.id, p.id, h.region);
        const folders = top.filter(f => f.objectType === "folders");
        const projectFiles = folders.find(f => /project files/i.test(f.name));
        const target = projectFiles ?? folders[0];
        if (target) {
          await loadFolderContents(h, p, target, []);
        } else {
          st.setSidebarFolders([]);
          st.setFileRows([]);
        }
      } catch (e) {
        st.setError(readError(e));
      } finally {
        st.setLoadingKind(null);
      }
    },
    [dm, loadFolderContents, loadProjectDetails]
  );

  const loadHubs = useCallback(async () => {
    const st = useAccBrowseStore.getState();
    st.setLoadingKind("hubs");
    st.setError(null);
    try {
      const rows = await dm.getAccHubs();
      st.setHubs(rows);
      const defaultHub = pickDefaultAccHub(rows);
      if (defaultHub) {
        st.setHub(defaultHub);
        const projs = await dm.getHubProjects(defaultHub.id, defaultHub.region);
        st.setProjects(projs);
        st.setProject(null);
      } else if (rows.length > 0) {
        st.setError(
          "No connected hubs yet — account admins must add Forma Code Compliance under Custom integrations before projects can load."
        );
      }
    } catch (e) {
      st.setError(readError(e));
    } finally {
      st.setLoadingKind(null);
      st.setInitialized(true);
    }
  }, [dm]);

  /** Re-fetch hub + project lists; keep current hub/project when still available. */
  const refreshHubsAndProjects = useCallback(async () => {
    const st = useAccBrowseStore.getState();
    const prevHubId = st.hub?.id;
    const prevProjectId = st.project?.id;
    st.setLoadingKind("hubs");
    st.setError(null);
    try {
      const rows = await dm.getAccHubs();
      st.setHubs(rows);

      const hub =
        (prevHubId ? rows.find(h => h.id === prevHubId) : undefined) ??
        pickDefaultAccHub(rows);

      if (!hub) {
        st.setHub(null);
        st.setProjects([]);
        st.setProject(null);
        if (rows.length > 0) {
          st.setError(
            "No connected hubs yet — account admins must add Forma Code Compliance under Custom integrations before projects can load."
          );
        }
        return;
      }

      st.setHub(hub);
      const projs = await dm.getHubProjects(hub.id, hub.region);
      st.setProjects(projs);

      const project = prevProjectId
        ? projs.find(p => p.id === prevProjectId)
        : undefined;
      if (project) {
        st.setProject(project);
        void loadProjectDetails(hub, project);
      } else {
        st.setProject(null);
        if (prevProjectId) {
          st.setCrumbs([]);
          st.setFileRows([]);
          st.setSidebarFolders([]);
          st.setActiveFolderId(null);
          st.setSelectedItemId(null);
          st.setSelection(null);
        }
      }
    } catch (e) {
      st.setError(readError(e));
    } finally {
      st.setLoadingKind(null);
      st.setInitialized(true);
    }
  }, [dm, loadProjectDetails]);

  useEffect(() => {
    const st = useAccBrowseStore.getState();
    if (!st.initialized && !st.loading) void loadHubs();
  }, [loadHubs]);

  const selectHub = useCallback(
    async (hubId: string) => {
      const st = useAccBrowseStore.getState();
      const selectedHub = st.hubs.find(h => h.id === hubId);
      if (!selectedHub || selectedHub.dmProvisioned === false) return;
      st.resetBrowse();
      st.setHub(selectedHub);
      st.setLoadingKind("hubs");
      st.setError(null);
      try {
        const rows = await dm.getHubProjects(hubId, selectedHub.region);
        st.setProjects(rows);
        st.setProject(null);
      } catch (e) {
        st.setError(readError(e));
      } finally {
        st.setLoadingKind(null);
      }
    },
    [dm, openProject]
  );

  const selectProject = useCallback(
    async (projectId: string) => {
      const st = useAccBrowseStore.getState();
      if (!st.hub) return;
      const p = st.projects.find(row => row.id === projectId);
      if (!p) return;
      await openProject(st.hub, p);
    },
    [openProject]
  );

  const selectFile = useCallback((itemId: string) => {
    const st = useAccBrowseStore.getState();
    if (!st.hub || !st.project) return;
    const file = st.fileRows.find(r => r.itemId === itemId);
    if (!file) return;

    if (isPdfExtension(file.extension)) {
      const next = st.selectedItemId === itemId ? null : itemId;
      st.setSelectedItemId(next);
      if (next) {
        useDocumentViewerStore.getState().openDocument({
          projectId: st.project.id,
          itemId: file.itemId,
          fileName: file.fileName,
          hubRegion: dmRegionFromHub(st.hub.region),
        });
      } else {
        useDocumentViewerStore.getState().close();
      }
      return;
    }

    if (!isRevitExtension(file.extension)) return;

    const next = st.selectedItemId === itemId ? null : itemId;
    st.setSelectedItemId(next);
    useDocumentViewerStore.getState().close();
    syncSelection(st.hub, st.project, st.crumbs, st.fileRows, next);
  }, []);

  const refreshCurrentFolder = useCallback(async () => {
    const st = useAccBrowseStore.getState();
    if (!st.hub || !st.project) return;
    if (st.crumbs.length > 0) {
      const idx = st.crumbs.length - 1;
      await loadFolderContents(
        st.hub,
        st.project,
        st.crumbs[idx],
        st.crumbs.slice(0, idx)
      );
      return;
    }
    st.setLoadingKind("files");
    st.setError(null);
    try {
      const top = await dm.getProjectTopFolders(
        st.hub.id,
        st.project.id,
        st.hub.region
      );
      const folders = top.filter(f => f.objectType === "folders");
      const projectFiles = folders.find(f => /project files/i.test(f.name));
      const target = projectFiles ?? folders[0];
      if (target) {
        await loadFolderContents(st.hub, st.project, target, []);
      } else {
        st.setSidebarFolders([]);
        st.setFileRows([]);
      }
    } catch (e) {
      st.setError(readError(e));
    } finally {
      st.setLoadingKind(null);
    }
  }, [dm, loadFolderContents]);

  const navigateToCrumb = useCallback(
    async (index: number) => {
      const st = useAccBrowseStore.getState();
      if (!st.hub || !st.project) return;
      const crumb = st.crumbs[index];
      if (!crumb) return;
      await loadFolderContents(
        st.hub,
        st.project,
        crumb,
        st.crumbs.slice(0, index)
      );
    },
    [loadFolderContents]
  );

  const openSidebarFolder = useCallback(
    async (folder: AccFolderEntry) => {
      const st = useAccBrowseStore.getState();
      if (!st.hub || !st.project) return;
      await loadFolderContents(st.hub, st.project, folder, st.crumbs);
    },
    [loadFolderContents]
  );

  return {
    hubs,
    projects,
    hub,
    project,
    projectDetails,
    crumbs,
    sidebarFolders,
    fileRows,
    activeFolderId,
    selectedItemId,
    selection,
    loading,
    loadingKind,
    error,
    initialized,
    loadHubs,
    refreshHubsAndProjects,
    selectHub,
    selectProject,
    selectFile,
    refreshCurrentFolder,
    openSidebarFolder,
    navigateToCrumb,
  };
}

function readError(e: unknown): string {
  if (e instanceof DataManagementApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Request failed";
}
